import { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { Routes, Navigate, Route } from 'react-router-dom';

import { api } from 'api';
import { Loading } from 'components/Loading';
import { Maintenance } from 'features/maintenance';
import { useStore } from 'models/Provider';
import { RootStore } from 'models/RootStore';
import ForgotPasswordPage from 'pages/ForgotPassword';
import { PasswordSuccess } from 'pages/PasswordSuccess';
import { SetPasswordPage } from 'pages/SetPassword';
import SigninPage from 'pages/Signin';
import { IAuthenticatedUser } from 'types/authentication';

import { useSessionCheck, useWhoAmI } from './authentication-queries';

interface Props {
  children: ReactNode;
  rootStore: RootStore;
}

interface AuthenticatedContextDefn {
  user: IAuthenticatedUser;
  needsTwoFactor: boolean;
}

export const AuthenticatedContext = createContext({} as AuthenticatedContextDefn);

export function AuthenticationProvider(props: Props) {
  const { children, rootStore } = props;
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const { lang, setLanguage } = useStore();

  const sessionQuery = useSessionCheck();
  const whoAmIQuery = useWhoAmI({
    enabled: sessionQuery.data?.isAuthenticated || false,
    onSuccess: ({ language }) => {
      if (language && language !== lang) {
        setLanguage(language);
      }
    },
  });

  useEffect(() => {
    if (whoAmIQuery.data) {
      rootStore.setMe(whoAmIQuery.data);
    }
  }, [rootStore, whoAmIQuery.data]);

  if (sessionQuery.isSuccess) {
    if (!sessionQuery.data.isAuthenticated) {
      return (
        <Routes>
          <Route path="forgotpassword" element={<ForgotPasswordPage />} />
          <Route path="setpassword" element={<SetPasswordPage />} />
          <Route path="resetpassword" element={<SetPasswordPage isReset />} />
          <Route path="setpasswordsuccess" element={<PasswordSuccess />} />
          <Route path="resetpasswordsuccess" element={<PasswordSuccess isReset />} />
          <Route path="signin" element={<SigninPage setTwoFactor={setNeedsTwoFactor} />} />
          <Route path="*" element={<Navigate to="/signin" />} />
        </Routes>
      );
    } else {
      if (whoAmIQuery.isSuccess) {
        return (
          <AuthenticatedContext.Provider value={{ user: whoAmIQuery.data, needsTwoFactor }}>
            {children}
          </AuthenticatedContext.Provider>
        );
      }
    }
  }

  if (
    api.global.isRequestError(sessionQuery.error) &&
    sessionQuery.error.response?.status === 503
  ) {
    return <Maintenance />;
  }

  return <Loading />;
}

export function useAuthenticatedUser() {
  return useContext(AuthenticatedContext).user;
}

export function useNeedsTwoFactor() {
  return useContext(AuthenticatedContext).needsTwoFactor;
}
